import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface ErrorDistribution {
  type: string;
  displayName: string;
  count: number;
  category: string;
  severity: string;
}

interface ErrorTypeDistributionProps {
  errorDistribution: ErrorDistribution[];
  selectedOrgId: string | null;
  onViewFlows?: () => void;
}

export function ErrorTypeDistribution({ errorDistribution, selectedOrgId, onViewFlows }: ErrorTypeDistributionProps) {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white rounded-lg shadow p-6 flex flex-col" style={{ height: '400px' }}>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Error Types</h3>
      {errorDistribution.length > 0 ? (
        <>
          <div className="flex-1 overflow-hidden">
            <div className="space-y-3">
              {(() => {
                // Calculate how many items can fit (roughly 32px per item + spacing)
                const availableHeight = 280; // Approximate height available for list
                const itemHeight = 36; // Height per item including spacing
                const maxItems = Math.floor(availableHeight / itemHeight);
                const itemsToShow = Math.min(errorDistribution.length, maxItems);
                const hasMore = errorDistribution.length > itemsToShow;
                
                return (
                  <>
                    {errorDistribution.slice(0, itemsToShow).map((error, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
                        onClick={() => navigate(`/${selectedOrgId}/flows?errorType=${encodeURIComponent(error.type)}&context=errorTypes`)}
                        title={`View flows with ${error.displayName} errors`}
                      >
                        <div className="flex items-center flex-1">
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            error.severity === 'high' ? 'bg-red-500' :
                            error.severity === 'medium' ? 'bg-amber-500' :
                            'bg-blue-500'
                          }`} />
                          <span className="text-sm text-gray-900 truncate pr-2">{error.displayName}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-600">{error.count}</span>
                      </div>
                    ))}
                    {hasMore && (
                      <p className="text-xs text-gray-500 text-center pt-2">
                        +{errorDistribution.length - itemsToShow} more error types
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          {onViewFlows && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-center">
              <button
                onClick={onViewFlows}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View Related Flows
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">No errors to display</p>
        </div>
      )}
    </div>
  );
}